/* Question wording, answer values and branches ported from Backbone's /quiz. */
(() => {
  // Loaded after quiz-form.js (snippets/quiz-config.liquid), whose pieces every quiz page shares.
  const shared = window.MosesQuizForm;
  if (!shared) return;
  const { SUBMIT_CLASS, typographic, progress, contactLine } = shared;
  const IN_TREATMENT = "I'm currently in treatment";
  // Public site/list IDs only. RnkAh7 uses Klaviyo double opt-in: a 202 means
  // requested, never confirmed. Treatment dates remain owned by Backbone.
  async function requestTreatmentReminder(email) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch("https://a.klaviyo.com/client/subscriptions?company_id=TZwzVT", {
        method: "POST",
        credentials: "omit",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: "2026-10-15",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "moses treatment quiz | reminder opt-in v1",
              profile: { data: { type: "profile", attributes: {
                email,
                subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
              } } },
            },
            relationships: { list: { data: { type: "list", id: "RnkAh7" } } },
          },
        }),
      });
      if (response.status !== 202) throw new Error("Reminder request failed");
    } finally {
      clearTimeout(timer);
    }
  }
  const COMPLETIONS_LIST = 'Rwv2Fd';
  async function subscribeQuizEmail(email) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=TZwzVT', {
        method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal,
        headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
        body: JSON.stringify({ data: { type: 'subscription', attributes: {
          custom_source: 'moses quiz completions | plastic-free-retainer | email save',
          profile: { data: { type: 'profile', attributes: { email,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
          } } },
        }, relationships: { list: { data: { type: 'list', id: COMPLETIONS_LIST } } } } }),
      });
      if (response.status !== 202) throw new Error('Your results are saved, but email signup failed. Please try again.');
    } finally { clearTimeout(timeout); }
  }
  const SUBMISSION = { quiz_version: "purchase-v1", funnel_variant: "plastic-free-retainer" };
  const questions = [
    {
      key: "q1",
      question: "Have you ever worn braces, Invisalign, or clear aligners?",
      options: ["Yes", "No", IN_TREATMENT],
    },
    {
      key: "q2",
      question: "When did you finish treatment?",
      options: [
        "Within the last year",
        "1–3 years ago",
        "3–5 years ago",
        "5+ years ago",
      ],
    },
    {
      key: "qTreatmentType",
      question: "What type of orthodontic treatment did you have?",
      options: ["Braces", "Aligners", "Invisalign", "Lingual Braces", "Other"],
    },
    {
      key: "qTreatmentProvider",
      question: "Who did your treatment?",
      options: ["Dentist", "Orthodontist", "Online Service", "Other"],
      // What the option shows; the option itself is what Backbone receives, unchanged.
      display: {
        "Online Service": "Online service (e.g. Smileset, Candid, Smile Direct Club)",
      },
    },
    {
      key: "q3",
      question: "Do you currently wear a retainer?",
      options: [
        "Every night",
        "A few nights a week",
        "A couple nights a month",
        "Stopped wearing it",
        "Don't have one anymore",
      ],
    },
    {
      key: "q4",
      question: "How long have you had your current retainer?",
      options: [
        "Less than 6 months",
        "6 months – 1 year",
        "1–2 years",
        "2–5 years",
        "5+ years",
      ],
    },
    {
      key: "q5",
      question: "What are your biggest issues with your retainer?",
      freetext: true,
    },
    {
      key: "q6",
      question:
        "How much have you reduced microplastics in other parts of your life?",
      options: [
        "A lot: water bottles, food storage, personal care, the works",
        "Some: I've made a few changes",
        "A little: I'm aware but haven't done much",
        "None",
      ],
    },
    {
      key: "q8",
      question:
        "What worries you most about microplastics exposure?",
      hint: "Select all that apply",
      multi: true,
      options: [
        "Long-term accumulation in my body",
        "Hormone or fertility impact",
        "My kids' exposure",
        "Cognitive or brain impact",
        "Gut and digestive health",
        "Not sure. I'm here to learn",
      ],
    },
  ];
  const treatmentQuestion = {
    key: "q_treatment_end",
    question: "When do you expect to finish treatment?",
    hint: "An estimate is fine. Treatment dates can change.",
    monthYear: true,
  };
  const treatmentMonths = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const treatmentBucket = (year, month) => {
    const today = new Date();
    const monthsAway = (Number(year) - today.getFullYear()) * 12 + Number(month) - today.getMonth() - 1;
    if (monthsAway <= 3) return "Within the next 3 months";
    if (monthsAway <= 6) return "3–6 months";
    if (monthsAway <= 12) return "6–12 months";
    return "More than a year away";
  };
  const steps = [
    "Retainer history",
    "Wear patterns",
    "Health priorities",
    "Checking eligibility",
  ];
  // A buyer arriving from the cart only needs to confirm treatment and current
  // retainer use. The longer educational quiz remains available for ad visitors.
  const purchaseQuestions = [
    {
      ...questions[0],
      question: "Have you finished braces or aligner treatment?",
      display: { No: "I’ve never had braces or aligners" },
    },
    {
      key: "q3",
      question: "Do you currently wear a retainer?",
      options: [
        "I wear one regularly",
        "I wear one sometimes",
        "I lost it",
        "I stopped wearing it",
        "It no longer fits",
      ],
    },
  ];
  const lastWornQuestion = {
    key: "q_last_worn",
    question: "When did you last wear your retainer?",
    options: [
      "Within the past month",
      "1–3 months ago",
      "3–6 months ago",
      "6–12 months ago",
      "More than a year ago",
    ],
  };
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const supportEmail = (address, subject, body) =>
    `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  class MosesPlasticFreeQuiz extends HTMLElement {
    connectedCallback() {
      const native = window.MosesQuiz;
      if (!native) return;
      this.api = native;
      const current = native.read();
      this.state = current.funnelVariant === 'plastic-free-retainer' ? current : {};
      this.state = native.save({ ...this.state,
        answers: this.state.answers || {}, step: this.state.step || 0,
        screen: this.state.screen || 'question', adFlow: true, emailGate: false,
        checkoutIntent: false, funnelVariant: 'plastic-free-retainer',
        returnUrl: native.config.productUrl,
        qualified: this.state.qualified === true,
        attributes: this.state.attributes || null,
        eventId: this.state.eventId || null, emailEventId: this.state.emailEventId || null,
        email: this.state.email || '', submissionComplete: this.state.submissionComplete === true,
        emailSignupAccepted: this.state.emailSignupAccepted === true,
      });
      this.purchaseFlow = false;
      this.adFlow = true;
      const resumeWithoutEmail = false;
      this.state.answers ||= {};
      this.syncQuestions();
      this.state.step = Math.max(
        0,
        Math.min(this.activeQuestions.length - 1, Number(this.state.step) || 0),
      );
      // Never repeat a write just because a loading screen was reloaded.
      if (["analysis", "analysis-research"].includes(this.state.screen)) this.state.screen = "question";
      if (this.state.screen === "analysis-email") this.state.screen = "email-qualified";
      this.controller?.abort();
      this.controller = new AbortController();
      this.restoreSavedResults();
      // Browser Back can restore the frozen document without reconnecting the element.
      window.addEventListener('pageshow', () => {
        this.state = this.api.read();
        if (this.restoreSavedResults()) {
          this.submitting = false;
          this.render(false);
        }
      }, { signal: this.controller.signal });
      this.render(false);
      if (resumeWithoutEmail) void this.complete();
      if (this.state.screen === "qualified" && this.state.checkoutIntent)
        void this.finish();
      const home = document.querySelector(".quiz-header a");
      if (home && !home.dataset.quizBound) {
        home.dataset.quizBound = "";
        home.addEventListener("click", () =>
          this.persist({ checkoutIntent: false }),
        );
      }
      if (!this.hasSavedResults()) this.api.track("quiz_started", { total_questions: this.activeQuestions.length });
    }
    disconnectedCallback() {
      clearTimeout(this.advanceTimer);
      clearInterval(this.analysisTimer);
      this.controller?.abort();
    }
    persist(patch = {}) {
      this.state = this.api.save({ ...this.state, ...patch });
    }
    hasSavedResults() {
      return this.state.funnelVariant === 'plastic-free-retainer' &&
        this.state.submissionComplete === true && this.api.qualified() &&
        (!this.state.email || this.state.emailSignupAccepted === true);
    }
    restoreSavedResults() {
      if (!this.hasSavedResults()) {
        if (this.state.screen === 'saved') this.persist({ screen: 'research' });
        return false;
      }
      this.persist({ screen: 'saved' });
      return true;
    }
    renderSavedResults() {
      this.heading.textContent = 'Your results are saved.';
      const actions = el('div', '', 'pfr-result-form');
      const view = el('a', 'View my retainer →', SUBMIT_CLASS);
      view.href = this.retainerUrl();
      const edit = this.button('Edit my answers', () => {
        this.persist({ screen: 'question', step: 0 });
        this.syncQuestions();
        this.render();
      }, 'quiz-help-link pfr-result-skip');
      actions.append(view, edit);
      this.fields.append(actions);
    }
    get inTreatment() {
      return this.state.answers.q1 === IN_TREATMENT;
    }
    syncQuestions() {
      const wear=this.state.answers.q3;
      this.activeQuestions = [
        ...purchaseQuestions,
        ...(['I lost it', 'I stopped wearing it'].includes(wear) ? [lastWornQuestion] : []),
        questions[5],
        {
          ...questions[6],
          question: 'What bothers you about your current or last retainer?',
          hint: 'Optional. Tell us what you’d like to change.',
        },
        questions[7],
        questions[8],
      ];
    }
    question() {
      return this.inTreatment && this.activeQuestions[this.state.step]?.key === "q3"
        ? treatmentQuestion
        : this.activeQuestions[this.state.step];
    }
    valid() {
      const q = this.question(),
        answer = this.state.answers[q.key];
      if (q.monthYear) {
        if (answer === "Not sure") return true;
        const expected = this.state.answers.q_treatment_expected_month;
        const today = new Date();
        return /^\d{4}-(0[1-9]|1[0-2])$/.test(expected || "") &&
          `${expected}` >= `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
      }
      return (
        q.freetext ||
        (q.multi
          ? Array.isArray(answer) && answer.length > 0
          : q.options.includes(answer))
      );
    }
    button(text, action, className) {
      const button = el("button", text, className);
      button.type = "button";
      button.addEventListener("click", action);
      return button;
    }
    navButton(text, action, back) {
      const button = this.button("", action, back ? "quiz-back" : "quiz-next");
      const arrow = el("img");
      arrow.src = this.api.config.arrow;
      arrow.alt = "";
      arrow.width = 22;
      arrow.height = 8;
      const label = el("span", text);
      button.append(...(back ? [arrow, label] : [label, arrow]));
      return button;
    }
    backButton(action = () => this.back()) {
      return this.navButton("Back", action, true);
    }
    error(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
      if (message) this.errorEl.focus();
    }
    render(focus = true) {
      clearInterval(this.analysisTimer);
      clearTimeout(this.advanceTimer);
      this.advancing = false;
      this.replaceChildren();
      const screen = this.state.screen || "question";
      this.dataset.screen = screen;
      this.progress =
        screen === "question"
          ? progress(this.state.step + 1, this.inTreatment && !this.adFlow ? 2 : this.activeQuestions.length)
          : el("p", "", "quiz-progress");
      this.heading = el("h1", "", "quiz-heading");
      this.heading.id = "quiz-heading";
      this.heading.tabIndex = -1;
      this.fields = el("div", "", "quiz-fields");
      this.errorEl = el("p", "", "quiz-error");
      this.errorEl.hidden = true;
      this.errorEl.role = "alert";
      this.errorEl.tabIndex = -1;
      this.append(this.progress, this.heading, this.fields, this.errorEl);
      if (screen === "saved") this.renderSavedResults();
      else if (screen === "research") this.renderResearch();
      else if (screen === "success") this.renderSuccess();
      else if (screen === "qualified") this.renderQualified();
      else if (screen === "check-in") this.renderCheckIn();
      else if (screen.startsWith("email-")) this.renderEmail(screen);
      else if (["analysis", "analysis-email", "analysis-research"].includes(screen)) this.renderAnalysis();
      else this.renderQuestion();
      if (focus) this.heading.focus();
    }
    renderQuestion() {
      const q = this.question();
      this.heading.textContent = q.question;
      this.heading.dataset.question = q.key;
      if ((this.purchaseFlow || this.adFlow) && q.key === "q1") {
        const intro = el("p", "We need to make sure this product is right for you. After you order, you'll go through a short onboarding to help us retrieve your existing dental scan or arrange a new one.", "quiz-description quiz-intro");
        const explainer = el("details", "", "quiz-scan-explainer");
        explainer.append(el("summary", "What is a dental scan?"));
        const content = el("div", "", "quiz-scan-explainer-content");
        const figure = el("figure", "", "quiz-scan-explainer-figure");
        const image = el("img");
        image.src = this.api.config.dentalScanImage;
        image.alt = "A handheld dental scanner beside a screen showing a 3D model of teeth";
        image.loading = "lazy";
        image.width = 1448;
        image.height = 1086;
        figure.append(image);
        content.append(figure, el("p", "If you finished braces or aligner treatment and wear a retainer now, your dentist or orthodontist likely scanned your teeth to make it. A dental scan is a 3D model made with an intraoral scanner (wand-like device). With your permission, we’ll request that scan from your dental office. If you don’t have one, we’ll book you a new scan at a practice near you and cover the cost."));
        explainer.append(content);
        this.heading.after(intro);
        intro.after(explainer);
      }
      if (q.hint) this.heading.after(el("p", q.hint, "quiz-description is-muted"));
      if (this.state.emailGate && !this.adFlow && this.state.step === 0) {
        this.heading.after(el(
          "p",
          "Find out whether moses is right for you.",
          "quiz-description quiz-intro",
        ));
      }
      if (q.monthYear) {
        this.renderTreatmentMonth();
      } else if (q.freetext) {
        const textarea = el("textarea", "", "quiz-field");
        textarea.name = q.key;
        textarea.setAttribute("aria-labelledby", "quiz-heading");
        textarea.maxLength = 2000;
        textarea.value = this.state.answers[q.key] || "";
        textarea.addEventListener("input", () => {
          this.state.answers[q.key] = textarea.value;
          this.state.eventId = null; this.state.submissionComplete = false;
          this.state.qualified = false; this.state.attributes = null;
          this.persist();
        });
        this.fields.append(textarea);
      } else {
        const group = el("fieldset", "", "quiz-options");
        const legend = el("legend", q.question, "quiz-sr");
        group.append(legend);
        for (const option of q.options) {
          const label = el("label", "", "quiz-option");
          const input = el("input");
          input.type = q.multi ? "checkbox" : "radio";
          input.name = q.key;
          input.value = option;
          const answer = this.state.answers[q.key];
          input.checked = q.multi
            ? (answer || []).includes(option)
            : answer === option;
          input.addEventListener("change", () => {
            this.state.eventId = null; this.state.submissionComplete = false;
            this.state.qualified = false; this.state.attributes = null;
            if (q.key === "q1" && option !== this.state.answers.q1) {
              if (this.adFlow) {
                delete this.state.answers.q3;
                delete this.state.answers.q_last_worn;
                delete this.state.answers.q_treatment_end;
                delete this.state.answers.q_treatment_expected_month;
              } else this.state.answers = {};
              this.state.qualified = false;
              this.state.attributes = null;
              this.state.eventId = null;
              this.state.emailEventId = null;
            }
            if ((this.purchaseFlow || this.adFlow) && (q.key === "q3" || q.key === "q_last_worn") && option !== this.state.answers[q.key]) {
              this.state.qualified = false;
              this.state.attributes = null;
              this.state.eventId = null;
              this.state.emailEventId = null;
              if (q.key === "q3") {
                delete this.state.answers.q_last_worn;
                delete this.state.answers.q_retainer_fit;
              }
            }
            if (q.multi)
              this.state.answers[q.key] = [
                ...group.querySelectorAll("input:checked"),
              ].map((i) => i.value);
            else this.state.answers[q.key] = option;
            if ((this.purchaseFlow || this.adFlow) && q.key === "q3") this.syncQuestions();
            this.persist();
            this.next.disabled = !this.valid();
            if (!q.multi) {
              // Arrow-key radio navigation must not unexpectedly move focus to another screen.
              clearTimeout(this.advanceTimer);
              if (!this.keyboardChoice)
                this.advanceTimer = setTimeout(() => this.advance(), 180);
            }
          });
          input.addEventListener("keydown", () => {
            this.keyboardChoice = true;
          });
          label.addEventListener("pointerdown", () => {
            this.keyboardChoice = false;
          });
          label.append(input, el("span", q.display?.[option] ?? typographic(option)));
          group.append(label);
        }
        this.fields.append(group);
        if ((this.purchaseFlow || this.adFlow) && q.key === "q3") {
          const help = el("a", "None of these? Get help", "quiz-help-link");
          const subject = "Help choosing a retainer option";
          const body = "Hi moses,\n\nI’m considering the Unplastic Retainer, but none of the answers about my current retainer fits my situation. Can you help me understand the right next step?\n\nThanks,";
          help.href = supportEmail(
            this.api.config.contactEmail,
            subject,
            body,
          );
          this.fields.append(help);
        }
      }
      const nav = el("nav", "", "quiz-nav");
      nav.setAttribute("aria-label", "Quiz navigation");
      nav.append(this.backButton());
      this.next = this.navButton("Continue", () => this.advance(), false);
      this.next.disabled = !this.valid();
      nav.append(this.next);
      this.fields.append(nav);
    }
    renderTreatmentMonth() {
      const group = el("fieldset", "", "quiz-treatment-date");
      group.append(el("legend", "Expected treatment end date", "quiz-sr"));
      const fields = el("div", "", "quiz-treatment-date-fields");
      const monthLabel = el("label", "", "quiz-treatment-date-label");
      monthLabel.append(el("span", "Month"));
      const month = el("select", "", "quiz-field");
      month.name = "q_treatment_month";
      month.append(new Option("Choose month", ""));
      treatmentMonths.forEach((name, index) => month.append(new Option(name, String(index + 1).padStart(2, "0"))));
      month.value = this.state.answers.q_treatment_month || "";
      monthLabel.append(month);
      const yearLabel = el("label", "", "quiz-treatment-date-label");
      yearLabel.append(el("span", "Year"));
      const year = el("select", "", "quiz-field");
      year.name = "q_treatment_year";
      year.append(new Option("Choose year", ""));
      const currentYear = new Date().getFullYear();
      for (let value = currentYear; value <= currentYear + 10; value++)
        year.append(new Option(String(value), String(value)));
      year.value = this.state.answers.q_treatment_year || "";
      yearLabel.append(year);
      const restrictPastMonths = () => {
        const currentMonth = new Date().getMonth() + 1;
        for (const option of month.options) {
          option.disabled = Boolean(option.value) && year.value === String(currentYear) && Number(option.value) < currentMonth;
        }
        if (month.selectedOptions[0]?.disabled) month.value = "";
      };
      restrictPastMonths();
      fields.append(monthLabel, yearLabel);
      const unsureLabel = el("label", "", "quiz-treatment-unsure quiz-option");
      const unsure = el("input");
      unsure.type = "checkbox";
      unsure.name = "q_treatment_unknown";
      unsure.checked = this.state.answers.q_treatment_end === "Not sure";
      unsureLabel.append(unsure, el("span", "I’m not sure yet"));
      const sync = () => {
        const answers = this.state.answers;
        if (unsure.checked) {
          answers.q_treatment_end = "Not sure";
          answers.q_treatment_expected_month = "";
        } else {
          answers.q_treatment_month = month.value;
          answers.q_treatment_year = year.value;
          answers.q_treatment_expected_month = month.value && year.value
            ? `${year.value}-${month.value}` : "";
          answers.q_treatment_end = answers.q_treatment_expected_month
            ? treatmentBucket(year.value, month.value) : "";
        }
        month.disabled = year.disabled = unsure.checked;
        this.persist();
        this.next.disabled = !this.valid();
      };
      month.addEventListener("change", () => { unsure.checked = false; sync(); });
      year.addEventListener("change", () => { unsure.checked = false; restrictPastMonths(); sync(); });
      unsure.addEventListener("change", sync);
      month.disabled = year.disabled = unsure.checked;
      group.append(fields, unsureLabel);
      this.fields.append(group);
    }
    advance() {
      if (this.advancing || !this.valid()) return;
      clearTimeout(this.advanceTimer);
      this.advancing = true;
      const q = this.question();
      this.api.track("quiz_question_answered", {
        question_key: q.key,
        question_num: this.state.step + 1,
        total_questions: this.activeQuestions.length,
      });
      const disqualified = { qualified: false, attributes: null };
      if (q.key === "q1" && this.state.answers.q1 === "No") {
        this.persist({ screen: "email-no", ...disqualified });
      } else if (q.key === "q_treatment_end") {
        this.persist({ screen: "email-treatment", ...disqualified });
      } else if ((this.purchaseFlow || this.adFlow) && (
        (q.key === "q3" && this.state.answers.q3 === "It no longer fits") ||
        (q.key === "q_last_worn" && this.state.answers.q_last_worn !== "Within the past month")
      )) {
        this.persist({ screen: "check-in", ...disqualified });
      } else if (this.state.step === this.activeQuestions.length - 1) {
        this.persist({ screen: 'analysis-research' });
        void this.transition('forward');
        return;
      } else {
        this.persist({ step: this.state.step + 1, screen: "question" });
      }
      void this.transition("forward");
    }
    renderCheckIn() {
      this.renderEmail("email-followup");
    }
    renderNightGuard() {
      this.heading.textContent = "Looking for a night guard?";
      this.heading.after(el("p", "The Unplastic Retainer is for keeping teeth in place after braces or aligners. If you’ve never had orthodontic treatment, take a look at the Unplastic Night Guard we’re developing.", "quiz-description"));
      const nightGuard = el("a", "Explore the Night Guard", SUBMIT_CLASS);
      nightGuard.href = this.api.config.root + "pages/night-guard";
      this.fields.append(nightGuard, contactLine(this.api.config.contactEmail));
      const nav = el("nav", "", "quiz-nav");
      nav.append(this.backButton());
      this.fields.append(nav);
    }
    // Re-render behind the shared screen animation (quiz-form.js).
    transition(direction) {
      return shared.transition(this, direction, () => this.render());
    }
    back() {
      if (this.submitting) return;
      clearTimeout(this.advanceTimer);
      if ((this.state.screen || "").startsWith("email-") || this.state.screen === "check-in")
        this.persist({ screen: "question" });
      else if (this.state.step > 0)
        this.persist({ step: this.state.step - 1, screen: "question" });
      else {
        this.leave();
        return;
      }
      void this.transition("back");
    }
    leave() {
      this.persist({ checkoutIntent: false });
      location.assign(this.api.localUrl(this.state.returnUrl));
    }
    // Consent is confirmed by Klaviyo's email, never by the browser response.
    renderResearch() {
      this.heading.textContent = 'It’s worth asking what you wear every night.';
      const intro = el('p', '', 'quiz-description');
      const study = el('a', 'A 2026 study', 'pfr-quiz-reference');
      study.href = 'https://link.springer.com/article/10.1186/s40510-026-00608-3';
      study.target = '_blank';
      study.rel = 'noopener noreferrer';
      intro.append(study, document.createTextNode(' tested six orthodontic materials in artificial saliva. All six released tiny microplastic particles.'));
      this.heading.after(intro);
      const sources = {
        1: ['Blood: Leslie et al., 2022', 'https://doi.org/10.1016/j.envint.2022.107199'],
        2: ['Lungs: Jenner et al., 2022', 'https://doi.org/10.1016/j.scitotenv.2022.154907'],
        3: ['Placentas: Ragusa et al., 2021', 'https://doi.org/10.1016/j.envint.2020.106274'],
        4: ['Brains: Nihart et al., 2025', 'https://www.nature.com/articles/s41591-024-03453-1'],
        5: ['Breast milk: Ragusa et al., 2022', 'https://www.mdpi.com/2073-4360/14/13/2700'],
        6: ['IBD: Yan et al., fecal samples, 2022', 'https://pubs.acs.org/doi/10.1021/acs.est.1c03924'],
        7: ['Dementia: Nihart et al., 2025', 'https://www.nature.com/articles/s41591-024-03453-1'],
        8: ['Cardiovascular events: Marfella et al., 2024', 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822'],
      };
      const paragraph = text => {
        const copy = el('p', '', 'quiz-description');
        let previous = 0;
        for (const match of text.matchAll(/\[(\d)\]/g)) {
          copy.append(document.createTextNode(text.slice(previous, match.index)));
          const [title, href] = sources[match[1]];
          const reference = el('a', match[0], 'pfr-quiz-reference');
          reference.href = href;
          reference.target = '_blank';
          reference.rel = 'noopener noreferrer';
          reference.title = title;
          reference.setAttribute('aria-label', 'Read source ' + match[1] + ': ' + title);
          copy.append(reference);
          previous = match.index + match[0].length;
        }
        copy.append(document.createTextNode(text.slice(previous)));
        this.fields.append(copy);
      };
      // Approved copy supplied by the user; preserve verbatim.
      paragraph('Microplastics have been found in our blood [1], lungs [2], placentas [3], brains [4], and breast milk [5]. Every human tissue scientists have tested so far has come back contaminated.');
      paragraph('In diseased tissue samples of people with chronic illnesses (IBD [6], Dementia [7], heart disease [8]), microplastic prevalence is significantly higher than healthy tissue.');
      const form = el('form', '', 'pfr-result-form');
      const label = el('label', 'Your email address (optional)', 'quiz-label');
      label.htmlFor = 'pfr-result-email';
      const email = el('input', '', 'quiz-field');
      email.id = 'pfr-result-email';
      email.name = 'email';
      email.type = 'email';
      email.autocomplete = 'email';
      email.value = this.state.email || '';
      // Optional, with no placeholder or suggested response.
      email.required = false;
      const submit = el('button', 'Save my results and see if I’m a fit', SUBMIT_CLASS);
      submit.type = 'submit';
      const continueToProduct = async saveEmail => {
        if (this.submitting) return;
        const value = saveEmail ? email.value.trim() : '';
        if (value !== this.state.email) this.persist({ eventId: null, submissionComplete: false, emailSignupAccepted: false });
        const answers = { ...this.state.answers, funnel_variant: 'plastic-free-retainer' };
        if (value) answers.email_marketing_consent = answers.email_marketing_consent || {
          source: 'quiz_result_email_save', collected_at: new Date().toISOString(), list_id: COMPLETIONS_LIST,
        };
        else delete answers.email_marketing_consent;
        this.persist({ email: value, answers, emailCaptureSkipped: !value });
        submit.disabled = skip.disabled = email.disabled = true;
        submit.textContent = 'Saving…';
        try { await this.complete(); }
        finally {
          submit.disabled = skip.disabled = email.disabled = false;
          submit.textContent = 'Save my results and see if I’m a fit';
        }
      };
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (form.reportValidity()) void continueToProduct(true);
      });
      const skip = this.button('Skip and see if I’m a fit', () => void continueToProduct(false), 'quiz-help-link pfr-result-skip');
      form.append(label, email, submit, skip);
      this.fields.append(form);
      const nav = el('nav', '', 'quiz-nav');
      nav.append(this.backButton(() => { if (this.submitting) return; this.persist({ screen: 'question' }); this.render(); }));
      this.fields.append(nav);
    }
    renderSuccess() {
      if (this.state.followupRequested) {
        this.heading.textContent = "Our team will be in touch.";
        this.heading.after(el("p", "We’ve saved your email and quiz answers. Our team will reach out to help with your next steps. Before ordering a replacement, ask your dentist or orthodontist to check whether a new retainer is right for you.", "quiz-description"));
        const home = el("a", "Return to moses", SUBMIT_CLASS);
        home.href = this.api.config.root;
        this.fields.append(home, contactLine(this.api.config.contactEmail));
        return;
      }
      const hasFinishMonth = Boolean(this.state.answers.q_treatment_expected_month);
      const reminderRequested = this.inTreatment && this.state.treatmentReminderRequest === "pending";
      const reminderFailed = this.inTreatment && this.state.treatmentReminderRequest === "failed";
      const description = this.inTreatment
        ? hasFinishMonth
          ? "We’ve saved your email and expected finish month. Once your dentist or orthodontist confirms your treatment is complete, come back to see if the Unplastic Retainer is right for you."
          : "We’ve saved your email. Once your dentist or orthodontist confirms your treatment is complete, come back to see if the Unplastic Retainer is right for you."
        : "We’ll email you when new moses products launch.";
      this.heading.textContent = this.inTreatment ? "Your details are saved." : "You’re on the list.";
      this.heading.after(el("p", description, "quiz-description"));
      if (reminderRequested) {
        this.heading.textContent = "Check your inbox.";
        this.heading.after(el("p", hasFinishMonth
          ? "Confirm your email to get a reminder about a month before your expected finish month. If you’re already subscribed, you’re all set."
          : "Confirm your email to get retainer updates from moses. We’ll need your expected finish month before we can time a reminder. If you’re already subscribed, you’re all set.", "quiz-description"));
      }
      if (reminderFailed) {
        this.fields.append(el("p", "Your details are saved, but we couldn’t request your confirmation email. Try again to sign up for emails.", "quiz-description"));
        const retry = this.button("Try email sign-up again", async () => {
          if (this.submitting) return;
          this.submitting = true;
          retry.disabled = true;
          retry.textContent = "Requesting…";
          await this.requestReminder();
          this.submitting = false;
          this.render();
        }, SUBMIT_CLASS);
        this.fields.append(retry);
      }
      const home = el("a", "Return to moses", SUBMIT_CLASS);
      home.href = this.api.config.root;
      this.fields.append(home, contactLine(this.api.config.contactEmail));
    }
    async requestReminder() {
      try {
        await requestTreatmentReminder(this.state.email);
        this.persist({ treatmentReminderRequest: "pending" });
      } catch {
        // Saving quiz answers succeeded. A retry must only repeat the consent request.
        this.persist({ treatmentReminderRequest: "failed" });
      }
    }
    renderEmail(screen) {
      if (screen === "email-no") {
        this.renderNightGuard();
        return;
      }
      const isQualified = screen === "email-qualified";
      const inTreatment = screen === "email-treatment";
      const followup = screen === "email-followup";
      const submitLabel = isQualified ? "See my retainer" : inTreatment ? "Save my details" : "Get help from the moses support team";
      this.heading.textContent = isQualified ? "You’re a great fit for moses." : inTreatment
        ? "Get ready for your first retainer."
        : "Let’s make sure a replacement is right for you.";
      if (isQualified) this.heading.after(el("p", "Enter your email to save your answers and see your retainer options.", "quiz-description"));
      if (inTreatment)
        this.heading.after(el("p", "After your dentist or orthodontist confirms treatment is complete, you’ll need a retainer to help keep your teeth in place. Save your details and choose an email reminder below. At your next appointment, ask about getting an intraoral scan. Our team can help with questions or materials for your dentist.", "quiz-description"));
      if (followup) {
        this.heading.after(el("p", "Your teeth may have moved, or your retainer may no longer fit. Ask your dentist or orthodontist to check whether a replacement retainer is right for you before ordering.", "quiz-description"));
      }
      const form = el("form");
      if (followup) form.append(el("p", "Leave your email and our team will reach out to help with next steps.", "quiz-question"));
      const label = el("label", "Your email address", "quiz-sr");
      label.htmlFor = "quiz-email";
      const email = el("input", "", "quiz-field");
      email.id = "quiz-email";
      email.type = "email";
      email.name = "email";
      email.required = true;
      email.autocomplete = "email";
      email.value = this.state.email || "";
      email.addEventListener("input", () =>
        this.persist({ email: email.value }),
      );
      const submit = el("button", submitLabel, SUBMIT_CLASS);
      submit.type = "submit";
      form.append(label, email);
      let reminderConsent;
      if (inTreatment) {
        const consentLabel = el("label", "", "quiz-reminder-consent");
        reminderConsent = el("input");
        reminderConsent.type = "checkbox";
        reminderConsent.name = "treatment_reminder_consent";
        reminderConsent.checked = false;
        const consentText = el("span", this.state.answers.q_treatment_expected_month
          ? "Confirm your email to get a reminder about a month before your expected finish month. Unsubscribe anytime."
          : "Confirm your email to get retainer updates. We’ll need your expected finish month before we can time a reminder. Unsubscribe anytime.");
        consentLabel.append(reminderConsent, consentText);
        form.append(consentLabel);
      }
      form.append(submit);
      const back = this.backButton();
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (this.submitting || !form.reportValidity()) return;
        if (isQualified) {
          this.persist({ email: email.value.trim() });
          submit.disabled = email.disabled = back.disabled = true;
          submit.textContent = "Saving…";
          this.error("");
          try {
            await this.complete();
          } finally {
            submit.disabled = email.disabled = back.disabled = false;
            submit.textContent = submitLabel;
          }
          return;
        }
        this.submitting = true;
        const wantsReminder = inTreatment && reminderConsent?.checked === true;
        if (reminderConsent) reminderConsent.disabled = true;
        submit.disabled = true;
        email.disabled = true;
        back.disabled = true;
        submit.textContent = "Saving…";
        this.error("");
        this.persist({
          email: email.value.trim(),
          emailEventId: this.state.emailEventId || this.api.uuid(),
        });
        try {
          await this.api.post(
            "/v1/quiz-submissions",
              {
                  email: this.state.email,
                  answers: this.state.answers,
                  ...SUBMISSION,
                },
            this.state.emailEventId,
          );
          this.api.track("quiz_email_captured", {
            qualified: false,
            dq_reason: inTreatment ? "in-treatment" : "retainer-assessment",
            product_handle: "unplastic-retainer",
            eligibility_status: inTreatment ? "in-treatment" : "needs-dentist-review",
            capture_context: this.purchaseFlow ? "purchase" : this.adFlow ? "ad" : "quiz",
          });
          this.persist({ treatmentReminderRequest: null, followupRequested: followup });
          if (wantsReminder) await this.requestReminder();
          this.persist({ screen: "success" });
          this.render();
        } catch (error) {
          this.error(error.message || "Unable to save. Please try again.");
        } finally {
          this.submitting = false;
          submit.disabled = false;
          email.disabled = false;
          if (reminderConsent) reminderConsent.disabled = false;
          back.disabled = false;
          submit.textContent = submitLabel;
        }
      });
      this.fields.append(form);
      const nav = el("nav", "", "quiz-nav");
      nav.append(back);
      this.fields.append(nav);
    }
    renderAnalysis() {
      this.heading.textContent = this.purchaseFlow ? "Checking if you’re a fit" : "Analyzing your profile";
      const list = el("ol", "", "quiz-list");
      list.setAttribute("aria-label", "Checking eligibility");
      const checkSteps = this.purchaseFlow
        ? ["Treatment complete", "Retainer status", "Checking eligibility"]
        : this.adFlow
          ? ["Your treatment", "Your priorities", "Retainer status", "Checking eligibility"]
          : steps;
      this.analysisDone = new Promise((resolve) => { this.resolveAnalysis = resolve; });
      for (const step of checkSteps) {
        const row = el("li");
        row.append(el("span", "", "quiz-marker"), el("span", step));
        list.append(row);
      }
      this.fields.append(list);
      const beforeEmail = this.state.screen === "analysis-email";
      const beforeResearch = this.state.screen === "analysis-research";
      this.status = el("p", (beforeEmail || beforeResearch) ? "Reviewing your answers…" : "Saving your answers…", "quiz-description");
      this.status.role = "status";
      this.fields.append(this.status);
      let index = 0;
      clearInterval(this.analysisTimer);
      this.analysisTimer = setInterval(() => {
        if (index < checkSteps.length)
          list.children[index++].setAttribute("data-done", "");
        if (index === checkSteps.length) {
          clearInterval(this.analysisTimer);
          this.resolveAnalysis?.();
          this.resolveAnalysis = null;
          if (beforeResearch) {
            this.persist({ screen: "research" });
            void this.transition("forward");
          } else if (beforeEmail) {
            this.persist({ screen: "email-qualified" });
            this.api.track("quiz_email_gate_viewed", { funnel_variant: this.api.read().funnelVariant || "cleaner-retainer" });
            this.render();
          }
        }
      }, 500);
    }
    renderQualified() {
      this.qualifiedShownAt = performance.now();
      const mark = el("div", "", "quiz-fit-mark");
      mark.setAttribute("aria-hidden", "true");
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "0 0 80 80");
      svg.innerHTML = '<circle cx="40" cy="40" r="35"/><path d="m24 41 11 11 21-25"/>';
      mark.append(svg);
      this.heading.before(mark);
      this.heading.textContent = "You’re a fit.";
      this.fields.append(el("p", "We’ll help with your dental scan after you order.", "quiz-description"));
      this.status = el("p", "Getting checkout ready…", "quiz-fit-status");
      this.status.role = "status";
      this.fields.append(this.status);
    }
    async finishWithAnalysis() {
      this.persist({ screen: "analysis" });
      this.render();
      await this.analysisDone;
      await this.finish();
    }
    async complete() {
      if (this.submitting) return;
      this.submitting = true;
      this.persist({ eventId: this.state.eventId || this.api.uuid() });
      this.error('');
      try {
        if (!this.api.storageAvailable())
          throw new Error('Please allow session storage in your browser, then try again.');
        if (!(this.state.submissionComplete && this.api.qualified())) {
        const result = await this.api.post('/v1/quiz-submissions', {
          ...SUBMISSION, answers: this.state.answers, cart_handoff: true,
          ...(this.state.email ? { email: this.state.email } : {}),
        }, this.state.eventId);
        if (!result.qualified) {
          this.persist({ qualified: false, attributes: null, screen:
            result.held_reason === 'In Treatment' ? 'email-treatment' :
            result.held_reason === 'Retainer assessment needed' ? 'check-in' : 'email-no' });
          this.render();
          return;
        }
        if (!result.order_id || !result.cart_attributes?.onboarding_token ||
            result.cart_attributes.order_id !== result.order_id)
          throw new Error('Unable to prepare your purchase. Please try again.');
        this.persist({ qualified: true, attributes: result.cart_attributes, submissionComplete: true });
        this.api.track(this.state.email ? 'quiz_email_captured' : 'quiz_completed', {
          qualified: true, meta_success: true, event_id: this.state.eventId,
          funnel_variant: 'plastic-free-retainer', quiz_version: 'purchase-v1',
          total_questions: this.activeQuestions.length,
        });
        }
        if (this.state.email && !this.state.emailSignupAccepted) {
          await subscribeQuizEmail(this.state.email);
          this.persist({ emailSignupAccepted: true });
        }
        this.finish();
      } catch (error) {
        this.error(error.message || 'Unable to save. Please try again.');
      } finally { this.submitting = false; }
    }
    retainerUrl() {
      const destination = new URL(this.api.localUrl(this.api.config.productUrl), location.origin);
      if (window.Shopify?.theme?.role === 'unpublished') {
        destination.searchParams.set('preview_theme_id', '');
        destination.searchParams.set('pb', '0');
      }
      return destination.pathname + destination.search;
    }
    finish() {
      this.persist({ screen: 'saved' });
      location.assign(this.retainerUrl());
    }
    renderRetry(message, retry, back) {
      this.status.textContent = "Your answers are still here.";
      this.error(message || "Unable to save. Please try again.");
      this.fields
        .querySelectorAll(".quiz-submit, .quiz-nav")
        .forEach((node) => node.remove());
      this.fields.append(this.button("Try again", retry, SUBMIT_CLASS));
      const nav = el("nav", "", "quiz-nav");
      nav.append(this.backButton(back));
      this.fields.append(nav);
    }
  }
  if (!customElements.get("moses-plastic-free-quiz"))
    customElements.define("moses-plastic-free-quiz", MosesPlasticFreeQuiz);
})();
